import { parse, stringify } from 'yaml'
import type { ChainInputModel, ChainLinkModel, ChainModel, TriggerOn } from '@/types'

const TRIGGER_ONS: TriggerOn[] = ['success', 'always', 'manual']

/** Parse the chain YAML body into the builder model. Throws on invalid YAML. */
export function parseChainYaml(yamlContent: string): ChainModel {
  const doc = parse(yamlContent) as Record<string, unknown> | null

  const rawInputs = Array.isArray(doc?.inputs) ? (doc!.inputs as unknown[]) : []
  const inputs: ChainInputModel[] = rawInputs.map((raw) => {
    const i = (raw ?? {}) as Record<string, unknown>
    return {
      name: String(i.name ?? ''),
      type: String(i.type ?? 'text'),
      required: i.required === true,
      default: i.default != null ? String(i.default) : undefined,
      description: i.description != null ? String(i.description) : undefined,
      options: Array.isArray(i.options) ? (i.options as unknown[]).map(String) : undefined,
      multiSelect: i.multiSelect === true,
      contextType: i.contextType != null ? String(i.contextType) : undefined,
      connectorType: i.connectorType != null ? String(i.connectorType) : undefined,
    }
  })

  const rawLinks = Array.isArray(doc?.links) ? (doc!.links as unknown[]) : []
  const links: ChainLinkModel[] = rawLinks.map((raw) => {
    const l = (raw ?? {}) as Record<string, unknown>
    const triggerOn = TRIGGER_ONS.includes(l.triggerOn as TriggerOn) ? (l.triggerOn as TriggerOn) : 'success'
    const inputsObj = l.inputs && typeof l.inputs === 'object' ? (l.inputs as Record<string, unknown>) : {}
    return {
      name: String(l.name ?? ''),
      workflow: String(l.workflow ?? ''),
      triggerOn,
      inputs: Object.entries(inputsObj).map(([k, v]) => [k, String(v)] as [string, string]),
    }
  })

  return { inputs, links }
}

/** Serialize the builder model into canonical chain YAML body. */
export function serializeChainModel(model: ChainModel): string {
  const doc: Record<string, unknown> = {}

  if (model.inputs.length > 0) {
    doc.inputs = model.inputs.map((i) => {
      const o: Record<string, unknown> = { name: i.name, type: i.type }
      if (i.required) o.required = true
      if (i.default) o.default = i.default
      if (i.description) o.description = i.description
      if (i.options && i.options.length > 0) o.options = i.options
      if (i.multiSelect) o.multiSelect = true
      if (i.contextType) o.contextType = i.contextType
      if (i.connectorType) o.connectorType = i.connectorType
      return o
    })
  }

  doc.links = model.links.map((l) => {
    const o: Record<string, unknown> = { name: l.name, workflow: l.workflow, triggerOn: l.triggerOn }
    const inputPairs = l.inputs.filter(([k]) => k.trim() !== '')
    if (inputPairs.length > 0) o.inputs = Object.fromEntries(inputPairs)
    return o
  })

  return stringify(doc, { lineWidth: 0 })
}
